// import { useAppKitAccount, useAppKitProvider, useDisconnect } from '@reown/appkit/react';
// import { useUnit } from 'effector-react';
// import { BrowserProvider } from 'ethers';
import { Box, Checkbox, Dialog, DialogContent, FormControlLabel, Grid, Typography } from '@mui/material';
import { FC, memo, useCallback, useEffect, useState } from 'react';
// import { toast } from 'react-toastify';
// import { AuthApiService } from '../api/auth/auth.api.service';
// import { LoginRequest } from '../api/auth/auth.types';
import { WALLETS } from '../constants';
import { WalletLogo } from './WalletLogo';
import { useTranslations } from 'next-intl';
import { Wallet } from '@reown/appkit-wallet-button';
import { Wallet as WalletType } from '@shared/constants';
import LazyImage from '../components/LazyImage';
import { useAuth, useWallet } from '@entities/user/lib/hooks';
import { Button } from './Button';
import CircularProgress from '@mui/material/CircularProgress';
import { isAgreeChanged, useConnectWalletStore } from '@entities/user/model';
import { WalletReloadInfoModal } from './WalletReloadInfoModal';
import { styled } from '@shared/styles';
import { useShouldUseDeeplink } from '../hooks/useShouldUseDeeplink';
import { useProviders } from '@shared/hooks';
import { resetBrowserData } from '@shared/utils';

interface ConnectWalletDialogProps {
  open: boolean;
  onClose: () => void;
}
interface DeeplinkDialogProps {
  open: boolean;
  wallet: (typeof WALLETS)[0];
  onClose: () => void;
  className?: string;
}

let DeepLinkDialog: FC<DeeplinkDialogProps> = ({ open, wallet, onClose, className }) => {
  const { walletClickHandler } = useAuth();
  const t = useTranslations();
  const { shouldUseDeeplink } = useShouldUseDeeplink();

  return (
    <Dialog
      PaperProps={{
        sx: { zIndex: 2 }
      }}
      open={open}
      onClose={onClose}
      scroll='body'
      className={className}
    >
      <DialogContent
        sx={{
          px: { xs: 4, sm: 4 },
          pb: { xs: 4, sm: 4 },
          pt: { xs: '21px', sm: '21px' },

          position: 'relative',
          background: '#1C1929'
        }}
      >
        <Box className='walletsBlock'>
          <Box
            className='walletsBlockItem'
            onClick={async () => {
              await walletClickHandler(wallet.connector);
              onClose();
            }}
          >
            <WalletLogo
              onClick={() => {}}
              wallet='browser'
              ext='png'
              title='Browser'
              connector={wallet.connector as Wallet}
            />
            <Typography sx={{ color: 'white' }} variant='body2'>
              {t('common.wallet.useBrowser')}
            </Typography>
          </Box>
          <Box
            className='walletsBlockItem'
            onClick={() => {
              let link = wallet.deepLinkBase;

              if (wallet.connector === 'metamask' && shouldUseDeeplink(WalletType.metamask)) {
                link += window.location.href;
              }
              if (wallet.connector === 'trust' && shouldUseDeeplink(WalletType.trustWallet)) {
                const params = new URLSearchParams(window.location.search);

                const encoded = encodeURIComponent(`${window.origin}?${params.toString()}`);

                link += encoded;
              }

              window.location.href = link;
            }}
          >
            <WalletLogo
              onClick={() => {}}
              wallet={wallet.wallet}
              title={wallet.title}
              connector={wallet.connector as Wallet}
            />
            <Typography sx={{ color: 'white' }} variant='body2'>
              {t('common.wallet.useDeeplink')} {wallet.title} ({t('common.wallet.recommended')})
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

DeepLinkDialog = styled(DeepLinkDialog, {
  '& .walletsBlock': {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  '& .walletsBlockItem': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '25px',
    padding: '10px',
    borderRadius: '15px',
    background: 'rgba(0,0,0,0.2)'
  }
});

const ConnectWalletDialog: FC<ConnectWalletDialogProps> = ({ open, onClose }) => {
  const modalWidth = 450;

  const [deepLinkModalOpened, setDeepLinkModalOpened] = useState<boolean>(false);
  const [selectedWallet, setSelectedWallet] = useState<(typeof WALLETS)[0]>(null);

  const { walletClickHandler, isSignatureModal, handleSignMessage } = useAuth();
  const { isSignatureLoading, isAgree } = useConnectWalletStore();
  const { disconnect } = useWallet();
  const { shouldUseDeeplink, shouldSkipConnectionModal } = useShouldUseDeeplink();
  const { getFirstProviderWalletType } = useProviders();

  const t = useTranslations();
  const openPdf = () => {
    const url = '/terms_of_use_magnet.pdf';
    const win = window.open(url, '_blank');
    if (win) win.focus();
  };

  const closeSignatureModal = async () => {
    try {
      await disconnect();
    } catch {
      await resetBrowserData();
      window.location.reload();
    }
  };

  return (
    <>
      {isSignatureModal && (
        <Dialog
          PaperProps={{
            sx: { zIndex: 2 }
          }}
          open={isSignatureModal}
          onClose={() => {}}
          scroll='body'
          className='signature-modal'
        >
          <DialogContent
            sx={{
              padding: '20px',
              position: 'relative',
              background: '#1C1929'
            }}
          >
            <Typography
              align='center'
              color='white'
              sx={{ whiteSpace: 'pre-wrap', color: 'white', fontSize: '18px', mb: 2 }}
            >
              {t('common.wallet.waitingForSignature')}
            </Typography>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                textAlign: 'center',
                color: 'white',
                opacity: '0.5',
                fontSize: '13px'
              }}
            >
              {t('common.wallet.signDisclaimer')}
            </div>
            <Box sx={{ position: 'absolute', top: 20, right: 24, cursor: 'pointer' }} onClick={closeSignatureModal}>
              <LazyImage src={'/images/icons/landing/close.svg'} alt='close' width={24} height={24} />
            </Box>
            <Grid container justifyContent='center' sx={{ mt: 4 }}>
              {isSignatureLoading ? (
                <CircularProgress style={{ color: 'white' }} />
              ) : (
                // <Button onClick={setMessageToSign}>{t('common.wallet.sign')}</Button>
                <Button
                  onClick={handleSignMessage}
                  style={{
                    color: 'white',
                    cursor: 'pointer',
                    padding: '5px 15px',
                    borderRadius: 15,
                    background: '#ffffff1A'
                  }}
                >
                  {t('common.wallet.sign')}
                </Button>
              )}
            </Grid>
          </DialogContent>
        </Dialog>
      )}
      {deepLinkModalOpened && (
        <DeepLinkDialog
          open={deepLinkModalOpened && open}
          wallet={selectedWallet}
          onClose={() => {
            setDeepLinkModalOpened(false);
            setSelectedWallet(null);
          }}
        />
      )}
      <Dialog
        PaperProps={{
          sx: { zIndex: 1 }
        }}
        // open={open && !shouldSkipConnectionModal()}
        open={open}
        onClose={onClose}
        scroll='body'
        className='connect-wallet-dialog'
      >
        <DialogContent
          sx={{
            // px: { xs: 4, sm: 4 },
            // py: { xs: 4, sm: 4 },
            // pt: { xs: 4, sm: 4 },
            // pb: { xs: '27px', sm: '27px' },
            padding: '32px 32px 27px',
            position: 'relative',
            maxWidth: modalWidth,
            background: '#1C1929'
          }}
        >
          <Grid style={{ marginTop: -10 }} container>
            <Grid display='flex' justifyContent='space-between' width='100%'>
              <Typography sx={{ color: 'white' }} style={{ color: 'white' }} color='white' variant='h5'>
                {t('common.wallet.title')}
              </Typography>
              <LazyImage
                style={{ position: 'relative', top: '-5px', cursor: 'pointer' }}
                onClick={onClose}
                src={'/images/icons/landing/close.svg'}
                alt='close'
              />
            </Grid>
            <Typography sx={{ color: 'white' }} style={{ color: 'white' }} color='white' variant='body2'>
              {t('common.wallet.warning')}
            </Typography>
            <Typography variant='body2'>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      '& .MuiSvgIcon-root': {
                        color: 'white'
                      },
                      '& .MuiTouchRipple-root': {
                        color: 'white'
                      },
                      '& .PrivateSwitchBase-input': {
                        color: 'white'
                      }
                    }}
                    style={{ color: 'white' }}
                    onChange={() => isAgreeChanged(!isAgree)}
                    checked={isAgree}
                  />
                }
                label={
                  <Typography>
                    <span style={{ color: 'white' }}>{t('common.wallet.privacy_1')}</span>{' '}
                    <span onClick={openPdf} style={{ color: '#40BFFF' }}>
                      {t('common.wallet.privacy_2')}
                    </span>
                  </Typography>
                }
                sx={{ '& > p': { fontSize: '0.9375rem' } }}
              />
            </Typography>
            <Grid
              container
              sx={{
                opacity: isAgree ? 1 : 0.2,
                mt: 2,
                pointerEvents: isAgree ? 'auto' : 'none'
              }}
            >
              {/* TODO: use next line when filter ne */}
              {/* {(shouldSkipConnectionModal() ? WALLETS.filter(({wallet}) => wallet === getFirstProviderWalletType()) : WALLETS).map(({ wallet, title, connector, deepLinkBase }) => ( */}
              {WALLETS.map(({ wallet, title, connector, deepLinkBase }) => (
                <Grid
                  key={`${wallet}-${title}`}
                  item
                  xs={4}
                  md={3}
                  sx={{ display: 'flex', justifyContent: 'center', rowGap: '16px' }}
                >
                  <WalletLogo
                    onClick={args => {
                      if (deepLinkBase) {
                        // if (connector === 'metamask' && window?.ethereum === undefined) {
                        if (connector === 'metamask' && shouldUseDeeplink(WalletType.metamask)) {
                          setSelectedWallet(WALLETS.find(({ connector }) => connector === args));
                          setDeepLinkModalOpened(true);

                          return;
                        }
                        // if (
                        //   connector === 'trust' &&
                        //   window?.ethereum === undefined &&
                        //   (window as any)?.trustwallet === undefined
                        // ) {
                        if (connector === 'trust' && shouldUseDeeplink(WalletType.trustWallet)) {
                          setSelectedWallet(WALLETS.find(({ connector }) => connector === args));
                          setDeepLinkModalOpened(true);

                          return;
                        }
                      }
                      walletClickHandler(args);
                    }}
                    wallet={wallet}
                    title={title}
                    connector={connector as Wallet}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <WalletReloadInfoModal />
    </>
  );
};

export default memo(ConnectWalletDialog);
